import React from 'react';
import { Column, useTable } from 'react-table';
import { useHistory } from 'react-router-dom';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  TableBody,
  SxProps,
  Theme,
} from '@mui/material';
import { RemoveCircleOutline } from '@mui/icons-material';
import { Quote } from 'shared/types/quote';
import { routes } from 'shared/constants';
import { getTotalQuoteRevenue } from 'shared/helpers';
import { DollarCell } from 'shared/tables/DollarCell';
import { QueryPayloadOverride as QueryPayload } from '@insight2profit/api-connector/dist/services';
import { ColumnFilter, columnTypesMap as nonfilterColumns } from 'components/ListQuotes/ColumnFilter';

const tableStyles: SxProps<Theme> = { maxHeight: '100%' };

const tableRowStyles: SxProps<Theme> = {
  '&:hover': {
    backgroundColor: '#f2eee4',
    cursor: 'pointer',
  },
};

const stickySecondRowStyles: SxProps<Theme> = {
  position: 'sticky',
  top: 56, //height of first row
  backgroundColor: '#FFF',
  overflow: 'hidden',
};

interface QuoteTableProps {
  quotes: Required<Quote>[];
  filters: QueryPayload;
  handleSingleFilterChanged: (singleColFilter: { [key: string]: string }) => void;
  handleClearAllFilters: () => void;
}

export const QuoteTable = ({ quotes, filters, handleSingleFilterChanged, handleClearAllFilters }: QuoteTableProps) => {
  const columns = React.useMemo<Column<Required<Quote>>[]>(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
      },
      {
        Header: 'Customer #',
        accessor: 'customer_number',
      },
      {
        Header: 'Customer',
        accessor: 'customer_name',
      },
      {
        Header: 'Revenue',
        accessor: 'revenue',
        Cell: ({ row: { original } }) => <DollarCell value={original.revenue} />,
      },
      {
        Header: 'Created',
        accessor: 'createDate',
      },
      {
        Header: 'Created By',
        accessor: 'createdBy',
      },
    ],
    []
  );
  const data: Required<Quote>[] = React.useMemo(
    () =>
      quotes.map(q => ({
        ...q,
        createDate: q.createDate ? new Date(q.createDate).toLocaleDateString() : '-',
        customer_name: q.customer.customer_name,
        customer_number: q.customer.customer_number,
        revenue: getTotalQuoteRevenue(q) || 0,
      })),
    [quotes]
  );

  const tableInstance = useTable({ columns, data });
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;
  const history = useHistory();

  return (
    <TableContainer sx={tableStyles}>
      <Table {...getTableProps()} stickyHeader>
        <TableHead>
          {headerGroups.map(headerGroup => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              <TableCell padding='checkbox' className={'MuiTableCell-stickyHeader'}></TableCell>
              {headerGroup.headers.map(column => (
                <TableCell {...column.getHeaderProps()}>{column.render('Header')}</TableCell>
              ))}
            </TableRow>
          ))}
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              <TableCell padding='checkbox' sx={stickySecondRowStyles}>
                <Tooltip title='Clear all filters' placement='top'>
                  <span>
                    <IconButton
                      onClick={handleClearAllFilters}
                      disabled={Object.keys(filters).length === 0}
                      size='large'
                    >
                      <RemoveCircleOutline color={Object.keys(filters).length === 0 ? 'disabled' : 'primary'} />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
              {headerGroup.headers.map(column => {
                const filterVal = filters.document[column.id] ? filters.document[column.id][0].value : '';
                return (
                  <TableCell {...column.getHeaderProps()} sx={stickySecondRowStyles}>
                    {!(column.id in nonfilterColumns) && (
                      <ColumnFilter columnName={column.id} onChange={handleSingleFilterChanged} value={filterVal} />
                    )}
                  </TableCell>
                );
              })}
            </tr>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <TableRow
                {...row.getRowProps()}
                sx={tableRowStyles}
                onClick={() => {
                  history.push(`${routes.viewQuote.replace(':id', row.original.id)}`);
                }}
              >
                <TableCell padding='checkbox'></TableCell>
                {row.cells.map(cell => {
                  return <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>;
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
